// ─── Company ───────────────────────────────────────────────────────────────

export const mockCompany = {
  name: "Dupont SAS",
  siren: "123 456 789",
  nafCode: "6201Z",
  nafLabel: "Programmation informatique",
  address: "15 rue de la Paix, 75001 Paris",
  employees: "10-49",
  plan: "securite" as Plan,
  hasPJ: false,
  stripeStatus: "active",
  nextBillingDate: "1er février 2025",
  planPrice: 129,
};

export type Plan = "essentiel" | "securite" | "securite_pj";

export const PLANS: Record<Plan, { label: string; price: number; features: string[] }> = {
  essentiel: {
    label: "Essentiel",
    price: 79,
    features: ["Score de conformité", "Alertes réglementaires", "Ressources documentaires"],
  },
  securite: {
    label: "Sécurité",
    price: 129,
    features: ["Tout Essentiel", "Assistant IA juridique", "Assurance juridique de base"],
  },
  securite_pj: {
    label: "Sécurité + PJ",
    price: 189,
    features: ["Tout Sécurité", "Protection juridique complète", "Assistance prioritaire"],
  },
};

// ─── User ──────────────────────────────────────────────────────────────────

export const mockUser = {
  firstName: "Jean",
  lastName: "Dupont",
  email: "jean.dupont@dupont-sas.fr",
  phone: "+33 6 12 34 56 78",
  role: "dirigeant" as const,
  roleLabel: "Dirigeant / CEO",
  initials: "JD",
  challenges: ["conformite", "rh"] as string[],
};

// ─── Compliance Score ──────────────────────────────────────────────────────

export type ScoreDomain = {
  id: string;
  label: string;
  score: number;
  items: { label: string; ok: boolean; priority?: boolean }[];
};

export const mockScoreDomains: ScoreDomain[] = [
  {
    id: "travail",
    label: "Droit du travail",
    score: 85,
    items: [
      { label: "Contrats de travail conformes CDI/CDD", ok: true },
      { label: "Affichages obligatoires en place", ok: true },
      { label: "Registre du personnel à jour", ok: true },
      { label: "Mutuelle collective proposée", ok: false, priority: true },
      { label: "Accord de participation (>50 sal.)", ok: true },
    ],
  },
  {
    id: "rgpd",
    label: "RGPD & Données",
    score: 60,
    items: [
      { label: "Registre des traitements tenu", ok: true },
      { label: "Politique de confidentialité publiée", ok: true },
      { label: "DPO désigné si obligatoire", ok: false, priority: true },
      { label: "Procédure violation de données", ok: false, priority: true },
      { label: "Contrats sous-traitants RGPD", ok: false },
    ],
  },
  {
    id: "fiscal",
    label: "Fiscal & Comptable",
    score: 70,
    items: [
      { label: "TVA déclarée dans les délais", ok: true },
      { label: "Liasses fiscales déposées", ok: true },
      { label: "Provisions légales constituées", ok: false, priority: true },
      { label: "Comptes annuels approuvés", ok: true },
    ],
  },
  {
    id: "securite",
    label: "Hygiène & Sécurité",
    score: 55,
    items: [
      { label: "Document Unique des Risques (DUER)", ok: false, priority: true },
      { label: "Formation sécurité incendie", ok: false, priority: true },
      { label: "Affichages obligatoires sécurité", ok: true },
      { label: "Registre de sécurité à jour", ok: false },
    ],
  },
];

export const mockGlobalScore = 72;

export const mockScoreHistory = [
  { date: "Juil. 2024", score: 58 },
  { date: "Août 2024",  score: 61 },
  { date: "Sep. 2024",  score: 64 },
  { date: "Oct. 2024",  score: 67 },
  { date: "Nov. 2024",  score: 70 },
  { date: "Jan. 2025",  score: 72 },
];

// ─── Regulatory Alerts ─────────────────────────────────────────────────────

export type AlertSeverity = "critical" | "warning" | "info";

export type Alert = {
  id: string;
  title: string;
  summary: string;
  category: string;
  severity: AlertSeverity;
  publishedAt: string;
  deadline?: string;
  source: string;
  read: boolean;
  nafCodes: string[];
  body?: string;
};

export const mockAlerts: Alert[] = [
  {
    id: "1",
    title: "Règlement DORA — Obligations DSI en vigueur",
    summary: "Le règlement européen DORA est entré en application le 17 janvier 2025. Les entreprises numériques doivent désormais disposer d'un cadre de gestion des risques ICT.",
    category: "Digital",
    severity: "critical",
    publishedAt: "3 jan. 2025",
    deadline: "17 jan. 2025",
    source: "Journal Officiel de l'UE",
    read: false,
    nafCodes: ["6201Z", "6202A"],
    body: `Le règlement (UE) 2022/2554 sur la résilience opérationnelle numérique du secteur financier (DORA) est entré en application le 17 janvier 2025.

**Points clés :**
- Mise en place obligatoire d'un cadre de gestion des risques ICT
- Signalement des incidents majeurs aux autorités compétentes sous 72h
- Tests de résilience opérationnelle numérique annuels
- Gestion contractuelle des risques liés aux prestataires tiers

**Actions à mener :**
1. Réaliser un audit de vos systèmes d'information
2. Cartographier vos prestataires tiers critiques
3. Mettre à jour votre politique de gestion des risques
4. Former vos équipes aux procédures de signalement`,
  },
  {
    id: "2",
    title: "URSSAF — Nouvelle obligation déclarative T1 2025",
    summary: "Modification du calendrier de déclaration DSN pour les entreprises de 10 à 49 salariés à partir du 1er janvier 2025.",
    category: "Social",
    severity: "warning",
    publishedAt: "28 déc. 2024",
    deadline: "31 jan. 2025",
    source: "URSSAF",
    read: false,
    nafCodes: ["*"],
    body: `À compter du 1er janvier 2025, les entreprises de 10 à 49 salariés sont soumises à de nouvelles obligations de déclaration mensuelle.

**Ce qui change :**
- Déclaration Sociale Nominative (DSN) mensuelle obligatoire pour toutes les entreprises ≥ 10 salariés
- Date limite de transmission : le 15 de chaque mois (avant le 25)
- Pénalité : 1,5% du plafond mensuel de sécurité sociale par salarié concerné

**À vérifier :**
- Votre logiciel de paie est-il à jour ?
- Votre gestionnaire RH est-il informé ?`,
  },
  {
    id: "3",
    title: "Code du Travail — Clarifications sur le télétravail",
    summary: "La DREETS publie de nouvelles recommandations sur les obligations employeur en matière de télétravail régulier pour 2025.",
    category: "RH",
    severity: "info",
    publishedAt: "20 déc. 2024",
    deadline: undefined,
    source: "DREETS Île-de-France",
    read: true,
    nafCodes: ["*"],
    body: `La Direction Régionale de l'Économie, de l'Emploi, du Travail et des Solidarités (DREETS) d'Île-de-France précise les obligations des employeurs pour le télétravail régulier.

**Principales obligations rappelées :**
- Formalisation du télétravail dans l'accord collectif ou la charte
- Prise en charge des frais professionnels (forfait ou réels)
- Respect des plages horaires de déconnexion
- Entretien annuel incluant la charge de travail en télétravail`,
  },
  {
    id: "4",
    title: "Taux d'IS 2025 — Aménagements pour PME",
    summary: "Le projet de loi de finances 2025 confirme le maintien du taux réduit d'IS à 15% pour les PME jusqu'à 42 500 € de bénéfices.",
    category: "Fiscal",
    severity: "info",
    publishedAt: "15 déc. 2024",
    deadline: undefined,
    source: "Direction Générale des Finances Publiques",
    read: true,
    nafCodes: ["*"],
    body: `Le PLF 2025 confirme le maintien des dispositions fiscales favorables aux PME pour l'exercice 2025.

**Ce qui est confirmé :**
- Taux réduit d'IS à 15% pour les bénéfices ≤ 42 500 €
- Conditions : CA < 10M€, capital libéré et détenu à 75% par des personnes physiques
- Taux normal maintenu à 25% au-delà`,
  },
  {
    id: "5",
    title: "CNIL — Nouvelles lignes directrices cookies 2025",
    summary: "La CNIL publie une mise à jour de ses recommandations sur les cookies et traceurs, avec de nouvelles obligations pour les DPO.",
    category: "Données",
    severity: "warning",
    publishedAt: "10 déc. 2024",
    deadline: "30 juin 2025",
    source: "CNIL",
    read: false,
    nafCodes: ["6201Z", "6311Z"],
    body: `La Commission Nationale de l'Informatique et des Libertés (CNIL) met à jour ses lignes directrices sur les cookies et traceurs.

**Nouvelles obligations :**
- Le refus des cookies doit être aussi simple que l'acceptation
- Conservation des preuves de consentement pendant 1 an minimum
- Audit annuel obligatoire des cookies déposés

**Délai de mise en conformité :** 30 juin 2025`,
  },
  {
    id: "6",
    title: "Sécurité incendie — Obligation de formation annuelle",
    summary: "Rappel des obligations en matière de formation à la sécurité incendie pour les établissements recevant du public et les locaux professionnels.",
    category: "Sécurité",
    severity: "warning",
    publishedAt: "5 déc. 2024",
    deadline: "31 mar. 2025",
    source: "INRS",
    read: false,
    nafCodes: ["*"],
    body: `L'article R.4227-39 du Code du Travail impose une formation à la sécurité incendie pour tous les salariés.

**Obligations :**
- Formation initiale à l'embauche
- Recyclage annuel pour les équipiers de première intervention
- Exercice d'évacuation semestriel
- Tenue à jour du registre de sécurité`,
  },
];

// ─── Insurance ─────────────────────────────────────────────────────────────

export type ClaimStatus = "en_cours" | "traite" | "refuse";

export type InsuranceClaim = {
  id: string;
  date: string;
  description: string;
  amount?: string;
  status: ClaimStatus;
};

export const mockInsuranceStatus = {
  active: false,
  policyRef: "PJ-2025-001234",
  insurer: "Shield Protect",
  startDate: "1er janvier 2025",
  renewalDate: "31 décembre 2025",
  annualCeiling: "50 000 €",
  deductible: "500 €",
};

export const mockClaims: InsuranceClaim[] = [
  {
    id: "sin-001",
    date: "15 nov. 2024",
    description: "Litige commercial — impayé client (14 500 €)",
    amount: "14 500 €",
    status: "en_cours",
  },
  {
    id: "sin-002",
    date: "3 sep. 2024",
    description: "Contentieux prud'homal — ex-salarié",
    amount: undefined,
    status: "traite",
  },
];

// ─── Resources ─────────────────────────────────────────────────────────────

export type ResourceType = "article" | "template";

export type Resource = {
  slug: string;
  title: string;
  description: string;
  category: string;
  type: ResourceType;
  publishedAt: string;
  readTime?: string;
  pages?: number;
  featured?: boolean;
};

export const mockResources: Resource[] = [
  // Articles
  {
    slug: "rgpd-guide-pme",
    title: "RGPD pour les PME : les 10 étapes de mise en conformité",
    description: "Guide complet pour comprendre et appliquer le RGPD dans votre entreprise, étape par étape, sans jargon technique.",
    category: "Données",
    type: "article",
    publishedAt: "Jan 2025",
    readTime: "8 min",
    featured: true,
  },
  {
    slug: "duer-obligation",
    title: "Document Unique des Risques : obligations et bonnes pratiques",
    description: "Tout ce que vous devez savoir sur le DUER : qui est concerné, quand le mettre à jour et comment l'élaborer.",
    category: "Hygiène & Sécurité",
    type: "article",
    publishedAt: "Jan 2025",
    readTime: "6 min",
    featured: true,
  },
  {
    slug: "licenciement-procedure",
    title: "Licenciement : procédure légale et pièges à éviter",
    description: "De l'entretien préalable à la lettre de notification, maîtrisez chaque étape pour sécuriser votre procédure.",
    category: "RH",
    type: "article",
    publishedAt: "Déc 2024",
    readTime: "10 min",
  },
  {
    slug: "tva-pme",
    title: "Comprendre les régimes de TVA applicables aux PME",
    description: "Régime réel normal, simplifié, franchise en base : quel régime s'applique à votre entreprise en 2025 ?",
    category: "Fiscal",
    type: "article",
    publishedAt: "Déc 2024",
    readTime: "7 min",
  },
  {
    slug: "affichages-obligatoires",
    title: "Affichages obligatoires en entreprise : liste complète 2025",
    description: "Mise à jour 2025 des affichages légalement obligatoires dans vos locaux professionnels.",
    category: "Droit du travail",
    type: "article",
    publishedAt: "Nov 2024",
    readTime: "5 min",
  },
  {
    slug: "dora-reglement",
    title: "Règlement DORA : ce que les entreprises numériques doivent faire",
    description: "Entrée en vigueur le 17 janvier 2025, DORA impose de nouvelles obligations de résilience opérationnelle.",
    category: "Digital",
    type: "article",
    publishedAt: "Jan 2025",
    readTime: "9 min",
  },
  {
    slug: "protection-juridique",
    title: "Protection juridique professionnelle : à quoi ça sert vraiment ?",
    description: "Litiges commerciaux, contentieux sociaux, redressement fiscal — comment la PJ protège votre entreprise.",
    category: "Assurance",
    type: "article",
    publishedAt: "Oct 2024",
    readTime: "6 min",
  },
  {
    slug: "teletravail-obligations",
    title: "Télétravail : obligations de l'employeur en 2025",
    description: "Accord collectif, prise en charge des frais, déconnexion : le point sur vos obligations juridiques.",
    category: "RH",
    type: "article",
    publishedAt: "Déc 2024",
    readTime: "5 min",
  },
  // Templates
  {
    slug: "modele-cdi",
    title: "Modèle de contrat de travail CDI — Syntec 2025",
    description: "Contrat à durée indéterminée conforme à la convention collective Syntec, à personnaliser selon votre situation.",
    category: "RH",
    type: "template",
    publishedAt: "Jan 2025",
    pages: 8,
    featured: true,
  },
  {
    slug: "modele-duer",
    title: "Trame DUER — Document Unique d'Évaluation des Risques",
    description: "Modèle pré-rempli pour réaliser votre DUER, avec exemples de risques courants par secteur d'activité.",
    category: "Hygiène & Sécurité",
    type: "template",
    publishedAt: "Jan 2025",
    pages: 12,
    featured: true,
  },
  {
    slug: "modele-politique-confidentialite",
    title: "Politique de confidentialité RGPD — modèle SaaS/IT",
    description: "Politique de confidentialité conforme au RGPD pour les entreprises de services numériques, personnalisable.",
    category: "Données",
    type: "template",
    publishedAt: "Déc 2024",
    pages: 6,
  },
  {
    slug: "modele-cgv",
    title: "Conditions Générales de Vente B2B — prestataire de services",
    description: "CGV conformes au Code de Commerce, avec clauses de pénalités de retard, propriété intellectuelle et responsabilité.",
    category: "Commercial",
    type: "template",
    publishedAt: "Nov 2024",
    pages: 10,
  },
  {
    slug: "modele-lettre-licenciement",
    title: "Lettre de licenciement pour motif personnel — trame",
    description: "Modèle de lettre de licenciement à adapter selon le motif (faute, insuffisance), conforme aux exigences légales.",
    category: "RH",
    type: "template",
    publishedAt: "Nov 2024",
    pages: 3,
  },
  {
    slug: "registre-traitements",
    title: "Registre des traitements RGPD — modèle Excel",
    description: "Tableau de bord pour recenser et documenter tous vos traitements de données personnelles selon l'article 30 RGPD.",
    category: "Données",
    type: "template",
    publishedAt: "Oct 2024",
    pages: 4,
  },
];

// ─── Assistant ──────────────────────────────────────────────────────────────

export type ChatMessage = { role: "user" | "assistant"; content: string };

export type ConversationSummary = {
  id: string;
  title: string;
  preview: string;
  date: string;
  messages: ChatMessage[];
};

export const mockConversations: ConversationSummary[] = [
  {
    id: "conv-1",
    title: "Mise en conformité RGPD",
    preview: "Quelles sont mes obligations en matière de RGPD ?",
    date: "Aujourd'hui",
    messages: [
      { role: "user", content: "Quelles sont mes obligations en matière de RGPD ?" },
      {
        role: "assistant",
        content: "En tant qu'entreprise de programmation informatique (NAF 6201Z), vos principales obligations RGPD sont : tenir un registre des traitements, publier une politique de confidentialité, et informer vos salariés et clients sur l'utilisation de leurs données. Votre score RGPD actuel est de 60/100 — je vous recommande de prioriser la procédure en cas de violation de données, qui est manquante.",
      },
      { role: "user", content: "Dois-je obligatoirement nommer un DPO ?" },
      {
        role: "assistant",
        content: "Le DPO (Délégué à la Protection des Données) est obligatoire seulement si vous traitez des données sensibles à grande échelle, si vous effectuez une surveillance systématique des personnes, ou si vous êtes une autorité publique. Pour Dupont SAS (10-49 salariés, services IT), le DPO n'est pas légalement obligatoire — mais désigner un référent RGPD interne est fortement recommandé.",
      },
    ],
  },
  {
    id: "conv-2",
    title: "Licenciement d'un salarié",
    preview: "Comment gérer un licenciement conforme au droit du travail ?",
    date: "Hier",
    messages: [
      { role: "user", content: "Comment gérer un licenciement conforme au droit du travail ?" },
      {
        role: "assistant",
        content: "La procédure de licenciement pour motif personnel comprend 4 étapes clés : 1) Convocation à entretien préalable par lettre RAR (au moins 5 jours ouvrables avant), 2) Entretien préalable (le salarié peut être assisté), 3) Délai de réflexion de 2 jours ouvrables minimum, 4) Notification par lettre RAR motivée. Le préavis légal est d'1 mois pour 6 mois à 2 ans d'ancienneté, 2 mois au-delà. Vérifiez votre convention collective Syntec (NAF 6201Z) pour les dispositions spécifiques.",
      },
    ],
  },
  {
    id: "conv-3",
    title: "Document Unique des Risques",
    preview: "Dois-je rédiger un DUER pour mon entreprise ?",
    date: "12 jan. 2025",
    messages: [
      { role: "user", content: "Dois-je rédiger un DUER pour mon entreprise ?" },
      {
        role: "assistant",
        content: "Oui, le DUER est obligatoire pour toutes les entreprises dès le 1er salarié (art. R.4121-1 du Code du travail). Il doit inventorier les risques identifiés dans chaque unité de travail et être mis à jour au moins une fois par an. L'absence de DUER est une infraction pénale (contravention 5ème classe, 1 500 € par salarié). C'est actuellement votre point de conformité prioritaire en Hygiène & Sécurité (score : 55/100). Téléchargez notre trame DUER dans la section Ressources.",
      },
    ],
  },
];

export const ASSISTANT_MOCK_RESPONSES: { keywords: string[]; response: string }[] = [
  {
    keywords: ["rgpd", "données", "dpo", "cookies", "consentement", "traitement", "confidentialité", "cnil"],
    response: `**RGPD — Points essentiels pour Dupont SAS**

En tant qu'entreprise de programmation informatique (NAF 6201Z), voici vos obligations principales :

**1. Registre des traitements**
Vous devez tenir à jour un registre listant tous vos traitements (clients, salariés, prospects). Fortement recommandé pour toute PME.

**2. DPO (Délégué à la Protection des Données)**
Obligatoire uniquement si vous traitez des données sensibles à grande échelle. Pour une PME de 10-49 salariés, il est recommandé mais non obligatoire.

**3. Politique de confidentialité**
Informez vos utilisateurs et clients sur l'utilisation de leurs données (mentions légales, politique sur votre site).

**4. Durées de conservation**
- Données clients : 5 ans
- Données RH : 5 ans après départ du salarié
- Données prospects : 3 ans

⚠️ *Votre score RGPD est de 60/100. Deux actions prioritaires : établir une procédure violation de données et sécuriser vos contrats sous-traitants.*`,
  },
  {
    keywords: ["duer", "risques", "sécurité", "incendie", "formation", "accident", "hygiène"],
    response: `**Document Unique d'Évaluation des Risques (DUER)**

Le DUER est **obligatoire pour toutes les entreprises dès le 1er salarié** (article R.4121-1 du Code du travail).

**Ce qu'il doit contenir :**
- Inventaire des risques par unité de travail
- Évaluation de la gravité et de la fréquence
- Plan d'action avec mesures de prévention

**Mise à jour obligatoire :**
- Au moins une fois par an
- Après tout accident du travail ou aménagement important

**Sanctions :**
Absence de DUER = contravention de 5ème classe (1 500 € par salarié). En cas d'accident, la responsabilité pénale du dirigeant peut être engagée.

**⚡ Action recommandée :**
Votre score Hygiène & Sécurité est de 55/100. Téléchargez la trame DUER dans la section Ressources pour démarrer rapidement.`,
  },
  {
    keywords: ["licenciement", "rupture", "congé", "arrêt", "disciplinaire", "faute", "prud"],
    response: `**Procédure de licenciement — Droit du travail**

Le licenciement est strictement encadré. Voici les étapes pour un motif personnel :

1. **Convocation** à entretien préalable (lettre RAR, au moins 5 jours ouvrables avant)
2. **Entretien préalable** (le salarié peut être assisté d'un représentant)
3. **Délai de réflexion** : 2 jours ouvrables minimum avant notification
4. **Notification** par lettre RAR motivée

**Délais de préavis :**
- < 6 mois : selon convention collective
- 6 mois à 2 ans : 1 mois
- ≥ 2 ans : 2 mois

**Indemnités légales :**
- 1/4 de mois de salaire par année (10 premières années)
- 1/3 de mois au-delà

⚠️ *Convention collective applicable : Syntec (NAF 6201Z). Consultez un avocat pour tout licenciement complexe ou litigieux.*`,
  },
  {
    keywords: ["tva", "impôt", "is", "bénéfice", "comptable", "liasse", "déclaration", "fiscal", "taxe"],
    response: `**Obligations fiscales — Dupont SAS**

**TVA :**
- Régime réel normal : déclaration mensuelle (CA3)
- Régime simplifié (CA < 247 000 €) : déclaration annuelle + acomptes
- Taux normal : 20% | Taux réduit : 5,5% ou 10%

**Impôt sur les Sociétés (IS) :**
- Taux réduit **15%** sur les 42 500 premiers € de bénéfice (si CA < 10M€)
- Taux normal **25%** au-delà
- Acomptes trimestriels si IS N-1 > 3 000 €

**Obligations comptables :**
- Tenue de la comptabilité (livre-journal, grand livre)
- Comptes annuels à établir dans les 6 mois de la clôture
- Dépôt au greffe du tribunal de commerce

📅 *Vérifiez vos prochaines échéances sur impots.gouv.fr avec votre SIREN 123 456 789.*`,
  },
  {
    keywords: ["contrat", "cdi", "cdd", "prestataire", "cgv", "clause", "alternance", "stage"],
    response: `**Contrats essentiels pour Dupont SAS**

**Contrats de travail (Syntec) :**
- CDI : poste, rémunération, convention collective, lieu, durée du travail
- CDD : mêmes mentions + motif de recours + terme précis
- Clause de confidentialité : fortement recommandée dans l'IT

**Contrats commerciaux :**
- **CGV** : obligatoires entre professionnels — délais de livraison, pénalités de retard (min. 3× taux légal)
- **Contrat de prestation** : définit le périmètre, les jalons, et surtout la propriété intellectuelle

**⚠️ Propriété intellectuelle :**
Dans l'IT, la cession des droits d'auteur doit être **explicitement mentionnée** dans le contrat. Elle ne se présume jamais.

📁 *Retrouvez nos modèles CDI et CGV dans la section Ressources → Modèles.*`,
  },
  {
    keywords: ["assurance", "sinistre", "protection", "pj", "litige", "défense", "couverture"],
    response: `**Protection Juridique — Ce que couvre votre contrat Shield**

Votre plan Sécurité inclut une assurance juridique de base. Avec l'option PJ complète (+ 35 €/mois), vous bénéficiez de :

**Litiges couverts :**
- Litiges commerciaux (impayés, rupture de contrat)
- Contentieux sociaux (prud'hommes)
- Contrôles fiscaux et litiges avec l'administration
- Litiges avec prestataires et fournisseurs

**Plafond annuel :** 50 000 € par sinistre
**Franchise :** 500 € (supprimée pour les litiges RH)
**Délai de prise en charge :** 48h ouvrées après déclaration

**Pour déclarer un sinistre :**
Rendez-vous dans la section Assurance → "Déclarer un sinistre".

⚠️ *Rappel : seuls les sinistres dont le fait générateur est postérieur à la souscription sont couverts.*`,
  },
  {
    keywords: ["score", "conformité", "améliorer", "audit", "checklist", "actions"],
    response: `**Score de conformité Dupont SAS : 72/100**

Voici vos axes d'amélioration prioritaires :

**🔴 Actions urgentes :**
- Hygiène & Sécurité (55/100) : Rédiger le DUER et planifier la formation incendie
- RGPD (60/100) : Établir une procédure de gestion des violations de données

**🟡 À traiter rapidement :**
- Fiscal (70/100) : Constituer les provisions légales manquantes
- Droit du travail (85/100) : Proposer une mutuelle collective (obligation légale)

**Pour gagner des points rapidement :**
1. Téléchargez la trame DUER (Ressources → Modèles)
2. Activez la checklist RGPD (Ressources → Articles)
3. Contactez votre expert-comptable pour les provisions

📈 *Avec ces 4 actions, votre score pourrait atteindre 85/100 en 30 jours.*`,
  },
];

// ─── Insurance FAQ ──────────────────────────────────────────────────────────

export const insuranceFAQ = [
  {
    q: "Que couvre la Protection Juridique ?",
    a: "La PJ couvre vos litiges commerciaux, sociaux, fiscaux, administratifs et pénaux liés à votre activité professionnelle, dans la limite du plafond annuel.",
  },
  {
    q: "Comment déclarer un sinistre ?",
    a: "Connectez-vous à votre espace, cliquez sur « Déclarer un sinistre » et remplissez le formulaire en décrivant la situation et en joignant les documents utiles. Votre conseiller vous recontacte sous 48h.",
  },
  {
    q: "Quel est le délai de prise en charge ?",
    a: "Après déclaration, un conseiller juridique vous contacte sous 48h ouvrées pour évaluer votre dossier et vous proposer une solution adaptée.",
  },
  {
    q: "Y a-t-il une franchise ?",
    a: "Oui, une franchise de 500 € s'applique par sinistre, sauf pour les litiges liés au droit du travail où elle est supprimée.",
  },
  {
    q: "L'assurance couvre-t-elle les litiges antérieurs à la souscription ?",
    a: "Non, la PJ couvre uniquement les sinistres dont le fait générateur est postérieur à la date de prise d'effet du contrat.",
  },
];
